import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DollarSign, CreditCard, TrendingUp, PieChart } from "lucide-react";

export default function AdminSalesTab({ apiUrl, getAuthHeaders }) {
  const credFetch = (url, opts = {}) => fetch(url, { ...opts, credentials: "include" });

  const [salesAnalytics, setSalesAnalytics] = useState(null);

  useEffect(() => { loadSalesAnalytics(); }, []);

  const loadSalesAnalytics = async () => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/sales/analytics?days=30`, { headers: getAuthHeaders(), credentials: "include" });
      const data = await res.json();
      if (data.success) setSalesAnalytics(data);
    } catch (error) { console.error("Sales analytics error:", error); }
  };

  if (!salesAnalytics) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-sales-tab">
      <h2 className="text-2xl font-bold text-gray-900">Sales Analytics</h2>
      <div className="grid grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500">Total Revenue</p><p className="text-3xl font-bold text-green-600">{"\u20B9"}{salesAnalytics.summary.total_revenue.toLocaleString()}</p></div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center"><DollarSign className="w-6 h-6 text-green-600" /></div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500">Total Orders</p><p className="text-3xl font-bold text-blue-600">{salesAnalytics.summary.total_orders}</p></div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center"><CreditCard className="w-6 h-6 text-blue-600" /></div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500">Avg Order Value</p><p className="text-3xl font-bold text-violet-600">{"\u20B9"}{salesAnalytics.summary.avg_order_value.toFixed(0)}</p></div>
            <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center"><TrendingUp className="w-6 h-6 text-violet-600" /></div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500">Conversion Rate</p><p className="text-3xl font-bold text-orange-600">{salesAnalytics.summary.conversion_rate}</p></div>
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center"><PieChart className="w-6 h-6 text-orange-600" /></div>
          </div>
        </Card>
      </div>
      <Card className="p-6">
        <h3 className="font-semibold mb-4">Daily Revenue ({salesAnalytics.period})</h3>
        <div className="h-64 flex items-end gap-1">
          {salesAnalytics.daily_revenue.slice(-14).map((day, i) => {
            const maxRevenue = Math.max(...salesAnalytics.daily_revenue.map((d) => d.revenue), 1);
            const height = (day.revenue / maxRevenue) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center">
                <div className="w-full bg-gradient-to-t from-violet-600 to-violet-400 rounded-t" style={{ height: `${Math.max(height, 2)}%` }} title={`\u20B9${day.revenue.toLocaleString()}`} />
                <span className="text-xs text-gray-500 mt-1 -rotate-45 origin-left">{day.date.slice(5)}</span>
              </div>
            );
          })}
        </div>
      </Card>
      <div className="grid grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Revenue by Plan</h3>
          <div className="space-y-3">
            {salesAnalytics.plan_breakdown.map((plan, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div><p className="font-medium capitalize">{plan.plan}</p><p className="text-sm text-gray-500">{plan.orders} orders</p></div>
                <p className="text-lg font-bold text-green-600">{"\u20B9"}{plan.revenue.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Recent Transactions</h3>
          <div className="space-y-2">
            {salesAnalytics.recent_transactions.map((tx, i) => (
              <div key={i} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                <div><p className="text-sm font-medium">{tx.user_email}</p><p className="text-xs text-gray-500 capitalize">{tx.plan}</p></div>
                <div className="text-right">
                  <p className="font-medium">{"\u20B9"}{tx.amount}</p>
                  <Badge className={tx.status === "success" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>{tx.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
